import React, { useState, useMemo, Suspense } from 'react';
import { View, Text, StyleSheet, ActivityIndicator } from 'react-native';
import { NavigationTab, ChildNavProps } from '../types/navigation';
import { BottomNavigation } from '../components/BottomNavigation';
import { StoreProvider, useContentStore, useProfileStore } from '../contexts/StoreContext';
import { LocalStorageAdapter } from '../storage/LocalStorageAdapter';
import { useBundledContent } from '../hooks/useBundledContent';
import { useMilestoneSync } from '../hooks/useMilestoneSync';
import { appConfig } from '../config/configManager';

// ─── Web layout width constraints ────────────────────────────────────────────
// Tune these two values to adjust the max-width of headers/tabs and content
// cards across all shared views (ConcernsView, MilestonesView, GuidanceView,
// WellVisitView, etc.).
appConfig.setHeaderWidthMax(850);
appConfig.setSingleCardMaxWidth(750);

// Lazy-load all views so a crash in one view doesn't blank the whole app
const HomeView       = React.lazy(() => import('../components/views/HomeView').then(m => ({ default: m.HomeView })));
const MilestonesView = React.lazy(() => import('../components/views/MilestonesView').then(m => ({ default: m.MilestonesView })));
const ConcernsView   = React.lazy(() => import('../components/views/ConcernsView').then(m => ({ default: m.ConcernsView })));
const WellVisitView  = React.lazy(() => import('../components/views/WellVisitView').then(m => ({ default: m.WellVisitView })));
const ActivitiesView = React.lazy(() => import('../components/views/ActivitiesView').then(m => ({ default: m.ActivitiesView })));
const GuidanceView   = React.lazy(() => import('../components/views/GuidanceView').then(m => ({ default: m.GuidanceView })));
const ResourceListView = React.lazy(() => import('../components/views/ResourceListView').then(m => ({ default: m.ResourceListView })));
const ActionsView    = React.lazy(() => import('../components/views/ActionsView').then(m => ({ default: m.ActionsView })));
const ConnectionsView= React.lazy(() => import('../components/views/ConnectionsView').then(m => ({ default: m.ConnectionsView })));
const SettingsView   = React.lazy(() => import('../components/views/SettingsView').then(m => ({ default: m.SettingsView })));
const AccountView    = React.lazy(() => import('../components/views/AccountView').then(m => ({ default: m.AccountView })));
const MoreView           = React.lazy(() => import('../components/views/MoreView').then(m => ({ default: m.MoreView })));
const ManageProfilesView = React.lazy(() => import('../components/views/ManageProfilesView').then(m => ({ default: m.ManageProfilesView })));

const ViewLoader = () => (
  <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
    <ActivityIndicator size="large" color="#3498db" />
  </View>
);

interface ViewErrorBoundaryState { hasError: boolean; error?: Error }
class ViewErrorBoundary extends React.Component<React.PropsWithChildren<{ tabName: string }>, ViewErrorBoundaryState> {
  constructor(props: any) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError(error: Error) { return { hasError: true, error }; }
  componentDidUpdate(prevProps: React.PropsWithChildren<{ tabName: string }>) {
    // Reset error state when navigating to a different tab
    if (prevProps.tabName !== this.props.tabName && this.state.hasError) {
      this.setState({ hasError: false, error: undefined });
    }
  }
  render() {
    if (this.state.hasError) {
      const err = this.state.error;
      return (
        <View style={{ flex: 1, padding: 20, backgroundColor: '#fff3cd' }}>
          <Text style={{ fontSize: 16, fontWeight: 'bold', color: '#856404', marginBottom: 8 }}>
            ⚠️ {this.props.tabName} view error
          </Text>
          <Text style={{ fontSize: 13, color: '#856404', marginBottom: 12 }}>
            {err?.message || 'Unknown error'}
          </Text>
          <Text style={{ fontSize: 11, color: '#6c757d', fontFamily: 'monospace' }}>
            {err?.stack?.split('\n').slice(0, 8).join('\n')}
          </Text>
        </View>
      );
    }
    return this.props.children;
  }
}

export const ChildNavWeb: React.FC<ChildNavProps> = ({
  initialTab = 'ChildNav',
  apiService,
  storage: storageProp,
}) => {
  const storage = useMemo(() => storageProp ?? new LocalStorageAdapter(), [storageProp]);

  // Initialize API service with stored auth token on mount
  React.useEffect(() => {
    if (apiService && (apiService as any).initializeWithStorage) {
      (apiService as any).initializeWithStorage(storage);
    }
  }, [apiService, storage]);

  return (
    <StoreProvider storage={storage} apiService={apiService}>
      <ChildNavWebContent initialTab={initialTab} apiService={apiService} storage={storage} />
    </StoreProvider>
  );
};

// Inner component rendered inside StoreProvider so it can access context hooks (e.g. useContentStore)
const ChildNavWebContent: React.FC<{
  initialTab: NavigationTab;
  apiService: any;
  storage: any;
}> = ({ initialTab, apiService, storage }) => {
  const [activeTab, setActiveTab] = useState<NavigationTab>(initialTab);
  const [selectedMilestonesDomain, setSelectedMilestonesDomain] = useState<string | null>(null);
  const [selectedConcernsDomain, setSelectedConcernsDomain] = useState<string | null>(null);

  // useContentStore() is a context hook — only works inside StoreProvider
  const contentStore = useContentStore();

  // Get active profile so views that need profileId/accountId (e.g. WellVisitView) receive the real values
  const profileStoreHook = useProfileStore();
  const activeProfile = profileStoreHook ? profileStoreHook((state: any) => {
    return state.profiles.find((p: any) => p.id === state.activeProfileId) || null;
  }) : null;

  // Initialize bundled content on web startup — loads from /assets/content/bundled-content.json,
  // adding missing records and updating any records whose version has changed.
  const stableContentStore = React.useMemo(() => contentStore, [contentStore]);
  const { status: bundleStatus } = useBundledContent(
    storage,
    stableContentStore,
    !!storage && !!stableContentStore  // Auto-initialize when both are available
  );

  // Keep milestone storage (AdminSegment 11255 standard, 11270 premium) in sync
  // whenever bundled content loads or its version changes.
  useMilestoneSync(storage, stableContentStore, bundleStatus);

  // Read content records from the Zustand store so we can offer a contentService adapter to views
  // that need to read AdminSegment data (e.g. DebugView milestone reload buttons).
  const contentRecords: any[] = contentStore ? contentStore((state: any) => state.records) : [];

  // Thin contentService adapter backed by the local content store.
  // Provides the same getContent(contentType, contentTypeId, languageTypeId) interface expected
  // by AdminSegmentStorage without requiring a live backend connection.
  const storeContentService = React.useMemo(() => ({
    getContent: async (contentType: string, contentTypeId: string, languageTypeId: string) => {
      const record = contentRecords?.find((r: any) =>
        r.contentType === contentType &&
        r.contentTypeId === contentTypeId &&
        r.languageTypeId === languageTypeId
      );
      return record?.tableData ?? null;
    }
  }), [contentRecords]);

  const [selectedActionsGroup, setSelectedActionsGroup] = React.useState<string | undefined>(undefined);
  const [snapshotReturnPending, setSnapshotReturnPending] = React.useState(false);
  const [wellVisitInitialTab, setWellVisitInitialTab] = React.useState<'Snapshot' | undefined>(undefined);

  React.useEffect(() => {
    if (!['Milestones', 'Concerns', 'Actions'].includes(activeTab)) {
      setSnapshotReturnPending(false);
    }
    if (activeTab !== 'Well-Visit') {
      setWellVisitInitialTab(undefined);
    }
  }, [activeTab]);

  const navigateFromSnapshot = (tab: NavigationTab) => { setSnapshotReturnPending(true); setActiveTab(tab); };
  const handleBackToSnapshot = () => { setSnapshotReturnPending(false); setWellVisitInitialTab('Snapshot'); setActiveTab('Well-Visit'); };

  const navigateWithDomain = (tab: NavigationTab, domain?: string) => {
    if (domain) {
      if (tab === 'Concerns') setSelectedConcernsDomain(domain);
      else if (tab === 'Actions') setSelectedActionsGroup(domain);
      else setSelectedMilestonesDomain(domain);
    }
    setActiveTab(tab);
  };

  const viewReportInNewTab = (html: string, filename: string) => {
    const fullHtml = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${filename}</title>
<style>
  body { margin: 0; padding: 16px; font-family: -apple-system, BlinkMacSystemFont, sans-serif; font-size: 14px; color: #2c3e50; line-height: 1.5; }
  * { box-sizing: border-box; }
  #pdf-print-btn { position: fixed; top: 12px; right: 16px; background: #3498db; color: #fff; border: none; border-radius: 6px; padding: 8px 18px; font-size: 14px; font-weight: 600; cursor: pointer; z-index: 9999; box-shadow: 0 2px 8px rgba(0,0,0,0.18); }
  #pdf-print-btn:hover { background: #2980b9; }
  @media print { #pdf-print-btn { display: none; } }
</style>
</head>
<body>
<button id="pdf-print-btn" onclick="window.print()">🖨️ Print / Save as PDF</button>
${html}
</body>
</html>`;
    const blob = new Blob([fullHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  };

  const renderCurrentView = () => {
    switch (activeTab) {
      case 'ChildNav':
        return <HomeView activeTab={activeTab} onTabChange={setActiveTab} onNavigateWithDomain={navigateWithDomain} storage={storage} bundleStatus={bundleStatus} />;
      case 'Milestones':
        return <MilestonesView selectedDomain={selectedMilestonesDomain} showBackToSnapshot={snapshotReturnPending} onBackToSnapshot={handleBackToSnapshot} onUpdateConcerns={() => setActiveTab('Concerns')} />;
      case 'Concerns':
        return <ConcernsView initialDomainKey={selectedConcernsDomain ?? undefined} showBackToSnapshot={snapshotReturnPending} onBackToSnapshot={handleBackToSnapshot} onUpdateActions={() => setActiveTab('Actions')} />;
      case 'Well-Visit':
        return <WellVisitView storage={storage} useContentStore={contentStore} apiService={apiService} onTabChange={setActiveTab}
          onSnapshotTabChange={navigateFromSnapshot}
          initialTab={wellVisitInitialTab}
          profileId={activeProfile?.id || 'default_profile'}
          accountId={activeProfile?.accountnum || 'default_account'}
          onViewPdf={viewReportInNewTab} />;
      case 'Activities':
        return <ActivitiesView />;
      case 'Guidance':
        return <GuidanceView />;
      case 'ResourceList':
        return <ResourceListView onTabChange={setActiveTab} />;
      case 'Actions':
        return <ActionsView initialGroup={selectedActionsGroup} onNavigateWithDomain={navigateWithDomain} showBackToSnapshot={snapshotReturnPending} onBackToSnapshot={handleBackToSnapshot} onHomeDashboard={() => setActiveTab('ChildNav')} />;
      case 'Connections':
        return <ConnectionsView storage={storage} apiService={apiService} />;
      case 'Settings':
        return <SettingsView storage={storage} apiService={apiService} />;
      case 'Account':
        return <AccountView storage={storage} apiService={apiService} />;
      case 'ManageProfiles':
        return <ManageProfilesView onTabChange={setActiveTab} storage={storage} />;
      case 'More':
        return <MoreView activeTab={activeTab} onTabChange={setActiveTab} storage={storage} apiService={apiService} useContentStore={contentStore} contentService={storeContentService} />;
      default:
        return <HomeView activeTab={activeTab} onTabChange={setActiveTab} storage={storage} bundleStatus={bundleStatus} />;
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <ViewErrorBoundary tabName={activeTab}>
          <Suspense fallback={<ViewLoader />}>
            {renderCurrentView()}
          </Suspense>
        </ViewErrorBoundary>
      </View>
      <BottomNavigation
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  content: {
    flex: 1,
  },
});


